import { useEffect, useRef, useState } from "react";
import { View, useWindowDimensions } from "react-native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import { Egg, Droplets, HeartHandshake } from "lucide-react-native";
import { useOnboarding } from "../OnboardingContext";
import OnboardingLayout, {
  ScreenTitle,
  ScreenSubtitle,
} from "../components/OnboardingLayout";
import GlassChoiceButton from "../components/GlassChoiceButton";
import { AUTO_ADVANCE_DELAY } from "../theme";
import { useTranslations } from "@src/packages/i18n";
import { getOnboardingViewport } from "../onboardingViewport";
import type {
  OnboardingStackParamList,
  TreatmentIntent,
  JourneyRole,
} from "../types";

type Props = NativeStackScreenProps<
  OnboardingStackParamList,
  "HowAreYouHelping"
>;

type InferredGender = "AFAB" | "AMAB";

type HelpOption = {
  value: "egg" | "sperm" | "carrier";
  labelKey: string;
  sublabelKey: string;
  Icon: typeof Egg;
  treatmentIntent: TreatmentIntent;
  journeyRole: Exclude<JourneyRole, "intended-parent" | null>;
  inferredGender: InferredGender;
};

const OPTIONS: HelpOption[] = [
  {
    value: "egg",
    labelKey: "onboarding.how_are_you_helping.egg_label",
    sublabelKey: "onboarding.how_are_you_helping.egg_sublabel",
    Icon: Egg,
    treatmentIntent: "egg-donation",
    journeyRole: "donor",
    inferredGender: "AFAB",
  },
  {
    value: "sperm",
    labelKey: "onboarding.how_are_you_helping.sperm_label",
    sublabelKey: "onboarding.how_are_you_helping.sperm_sublabel",
    Icon: Droplets,
    treatmentIntent: "sperm-donation",
    journeyRole: "donor",
    inferredGender: "AMAB",
  },
  {
    value: "carrier",
    labelKey: "onboarding.how_are_you_helping.carrier_label",
    sublabelKey: "onboarding.how_are_you_helping.carrier_sublabel",
    Icon: HeartHandshake,
    treatmentIntent: "surrogacy",
    journeyRole: "gestational-carrier",
    inferredGender: "AFAB",
  },
];

export default function HowAreYouHelpingScreen({ navigation }: Props) {
  const { width, height } = useWindowDimensions();
  const { condensed } = getOnboardingViewport(width, height);
  const compactChoices = condensed || width < 430;
  const t = useTranslations();
  const { data, updateField } = useOnboarding();
  const initialSelection =
    OPTIONS.find(
      (option) =>
        option.treatmentIntent === data.treatmentIntent &&
        option.journeyRole === data.journeyRole &&
        option.inferredGender === data.gender,
    )?.value ?? null;
  const [selected, setSelected] = useState<HelpOption["value"] | null>(
    initialSelection,
  );
  const advanceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const advancing = useRef(false);

  useEffect(
    () => () => {
      if (advanceTimer.current) clearTimeout(advanceTimer.current);
    },
    [],
  );

  const handleSelect = (opt: HelpOption) => {
    if (advancing.current) return;
    advancing.current = true;
    setSelected(opt.value);
    updateField("treatmentIntent", opt.treatmentIntent);
    updateField("journeyRole", opt.journeyRole);
    updateField("gender", opt.inferredGender);
    advanceTimer.current = setTimeout(() => {
      advancing.current = false;
      advanceTimer.current = null;
      navigation.navigate("Birthday");
    }, AUTO_ADVANCE_DELAY);
  };

  return (
    <OnboardingLayout>
      <ScreenTitle>{t("onboarding.how_are_you_helping.title")}</ScreenTitle>
      <ScreenSubtitle>
        {t("onboarding.how_are_you_helping.subtitle")}
      </ScreenSubtitle>
      <View style={{ gap: compactChoices ? 10 : 14 }}>
        {OPTIONS.map((opt) => (
          <GlassChoiceButton
            key={opt.value}
            Icon={opt.Icon}
            label={t(opt.labelKey)}
            sublabel={t(opt.sublabelKey)}
            selected={selected === opt.value}
            compact={compactChoices}
            onPress={() => handleSelect(opt)}
          />
        ))}
      </View>
    </OnboardingLayout>
  );
}
